import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { LinkedinIcon, ChevronDownIcon, GlobeIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { JoinNetworkForm } from './JoinNetworkForm';
import { leaders, Leader } from '../data/leaders';
function LeaderCard({
  leader,
  index
}: {
  leader: Leader;
  index: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const [bioOpen, setBioOpen] = useState(false);
  const {
    language,
    t
  } = useLanguage();
  const displayTitle = language === 'ES' ? leader.titleEs : leader.title;
  const displayBio = language === 'ES' ? leader.bioEs : leader.bio;
  return <motion.article ref={ref} initial={{
    opacity: 0,
    y: 50,
    rotate: index % 2 === 0 ? -2 : 2
  }} animate={inView ? {
    opacity: 1,
    y: 0,
    rotate: 0
  } : {}} transition={{
    duration: 0.7,
    delay: index * 0.1,
    ease: [0.16, 1, 0.3, 1]
  }} className={`bg-white rounded-2xl shadow-lg overflow-hidden border-l-4 ${leader.borderColor} group card-hover`} data-id="element-1826">
      <div className="p-6" data-id="element-1827">
        {leader.photo ? <div className="mb-4" data-id="element-1828">
            <motion.div whileHover={{
          scale: 1.02
        }} className={`w-full h-64 rounded-2xl bg-gradient-to-br ${leader.gradient} overflow-hidden shadow-lg`} data-id="element-1829">
              <img src={leader.photo} alt={leader.name} className="w-full h-full object-cover" style={{
            objectPosition: leader.name === 'Mario Ramos' ? 'center 25%' : leader.name === 'Sal Mendosa' ? 'center 20%' : 'center 15%',
            transform: leader.name === 'Mario Ramos' ? 'scale(1.4)' : leader.name === 'Sal Mendosa' ? 'scale(1.35)' : undefined,
            transformOrigin: leader.name === 'Mario Ramos' ? 'center 30%' : leader.name === 'Sal Mendosa' ? 'center 25%' : undefined
          }} data-id="element-1830" />
            </motion.div>
            <div className="mt-4" data-id="element-1831">
              <h3 className="font-bebas text-xl text-haa-navy tracking-wider leading-tight" data-id="element-1832">
                {leader.name}
              </h3>
              <p className={`font-nunito font-bold text-sm ${leader.accentColor} uppercase tracking-wider`} data-id="element-1833">
                {displayTitle}
              </p>
              {(leader.jobTitle || leader.jobTitleEs) && <p className="font-nunito text-xs text-haa-midblue font-semibold tracking-wide mt-0.5" data-id="element-1834">
                  {language === 'ES' ? leader.jobTitleEs : leader.jobTitle}
                </p>}
              {leader.confirmed === false && <span className="font-nunito text-xs text-gray-400 font-normal tracking-normal block mt-0.5" data-id="element-1835">
                  {language === 'ES' ? '(Confirmación Pendiente)' : '(Confirmation Pending)'}
                </span>}
            </div>
          </div> : <div className="flex items-start gap-4 mb-4" data-id="element-1836">
            <motion.div whileHover={{
          scale: 1.05,
          rotate: 5
        }} className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${leader.gradient} flex items-center justify-center flex-shrink-0 shadow-lg overflow-hidden`} data-id="element-1837">
              <span className="font-bebas text-white text-2xl tracking-wider" data-id="element-1838">
                {leader.initials}
              </span>
            </motion.div>
            <div className="flex-1 min-w-0" data-id="element-1839">
              <h3 className="font-bebas text-xl text-haa-navy tracking-wider leading-tight" data-id="element-1840">
                {leader.name}
              </h3>
              <p className={`font-nunito font-bold text-sm ${leader.accentColor} uppercase tracking-wider`} data-id="element-1841">
                {displayTitle}
              </p>
              {(leader.jobTitle || leader.jobTitleEs) && <p className="font-nunito text-xs text-haa-midblue font-semibold tracking-wide mt-0.5" data-id="element-1842">
                  {language === 'ES' ? leader.jobTitleEs : leader.jobTitle}
                </p>}
              {leader.confirmed === false && <span className="font-nunito text-xs text-gray-400 font-normal tracking-normal block mt-0.5" data-id="element-1843">
                  {language === 'ES' ? '(Confirmación Pendiente)' : '(Confirmation Pending)'}
                </span>}
            </div>
          </div>}

        {/* Bio Dropdown Toggle */}
        <button onClick={() => setBioOpen(!bioOpen)} aria-expanded={bioOpen} className={`w-full flex items-center justify-between gap-2 py-2.5 px-3 rounded-xl mb-1 text-left transition-colors duration-200 ${bioOpen ? 'bg-gray-50' : 'hover:bg-gray-50'}`} data-id="element-1844">
          <span className={`font-nunito font-semibold text-sm ${leader.accentColor}`} data-id="element-1845">
            {bioOpen ? t('leadership.hideBio') : t('leadership.readBio')}
          </span>
          <motion.div animate={{
          rotate: bioOpen ? 180 : 0
        }} transition={{
          duration: 0.25,
          ease: [0.16, 1, 0.3, 1]
        }} className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center bg-gradient-to-br ${leader.gradient}`} data-id="element-1846">
            <ChevronDownIcon className="w-3.5 h-3.5 text-white" data-id="element-1847" />
          </motion.div>
        </button>

        {/* Animated Bio */}
        <AnimatePresence initial={false} data-id="element-1848">
          {bioOpen && <motion.div key="bio" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.35,
          ease: [0.16, 1, 0.3, 1]
        }} className="overflow-hidden" data-id="element-1849">
              <p className="font-nunito text-gray-600 text-sm leading-relaxed pt-2 pb-4 px-1" data-id="element-1850">
                {displayBio}
              </p>
            </motion.div>}
        </AnimatePresence>

        {/* Social Links */}
        <div className="flex items-center gap-2 pt-4 border-t border-gray-100" data-id="element-1851">
          {leader.linkedin && <motion.a href={leader.linkedin} target="_blank" rel="noopener noreferrer" aria-label={`${leader.name} LinkedIn`} whileHover={{
          scale: 1.15
        }} whileTap={{
          scale: 0.9
        }} className={`w-8 h-8 rounded-lg bg-gradient-to-br ${leader.gradient} flex items-center justify-center`} data-id="element-1852">
              <LinkedinIcon className="w-4 h-4 text-white" data-id="element-1853" />
            </motion.a>}
          {leader.website && <motion.a href={leader.website} target="_blank" rel="noopener noreferrer" aria-label={`${leader.name} Website`} whileHover={{
          scale: 1.15
        }} whileTap={{
          scale: 0.9
        }} className={`w-8 h-8 rounded-lg bg-gradient-to-br ${leader.gradient} flex items-center justify-center`} data-id="element-1854">
              <GlobeIcon className="w-4 h-4 text-white" data-id="element-1855" />
            </motion.a>}
          {leader.website2 && <motion.a href={leader.website2} target="_blank" rel="noopener noreferrer" aria-label={`${leader.name} Featured Article`} whileHover={{
          scale: 1.15
        }} whileTap={{
          scale: 0.9
        }} className={`w-8 h-8 rounded-lg bg-gradient-to-br ${leader.gradient} flex items-center justify-center`} data-id="element-1856">
              <GlobeIcon className="w-4 h-4 text-white" data-id="element-1857" />
            </motion.a>}
        </div>
      </div>
    </motion.article>;
}
export function Leadership() {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, {
    once: true
  });
  const {
    t,
    language
  } = useLanguage();
  const [formOpen, setFormOpen] = useState(false);
  return <section id="leadership" className="bg-gray-50 py-10 lg:py-14" aria-labelledby="leadership-heading" data-id="element-1858">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1859">
        <div ref={titleRef} className="text-center mb-14" data-id="element-1860">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-1861">
            <span className="font-nunito text-haa-blue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-1862">
              {t('leadership.label')}
            </span>
            <h2 id="leadership-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-haa-navy tracking-wider mt-2" data-id="element-1863">
              {t('leadership.heading')}
            </h2>
            <div className="flex items-center justify-center gap-3 mt-4" data-id="element-1864">
              <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-1865" />
              <div className="w-3 h-3 rounded-full bg-haa-skyblue" data-id="element-1866" />
              <div className="w-12 h-1 bg-haa-midblue rounded-full" data-id="element-1867" />
            </div>
            <p className="font-nunito text-gray-600 text-lg mt-6 max-w-2xl mx-auto" data-id="element-1868">
              {t('leadership.description')}
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1869">
          {leaders.map((leader, i) => <LeaderCard key={leader.name} leader={leader} index={i} data-id="element-1870" />)}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={titleInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.8
      }} className="mt-16 text-center bg-gradient-to-r from-haa-blue via-haa-navy to-haa-blue rounded-3xl p-10 text-white" data-id="element-1871">
          <h3 className="font-bebas text-4xl sm:text-5xl tracking-wider mb-3" data-id="element-1872">
            {t('leadership.cta.heading')}
          </h3>
          <p className="font-nunito text-white/80 text-lg mb-6 max-w-2xl mx-auto leading-relaxed" data-id="element-1873">
            {language === 'ES' ? '¿Eres estudiante universitario, dueño de negocio, profesional, líder comunitario, quieres aprender más, o simplemente buscas conectar? Únete a nuestra red y ayuda a construir una plataforma compartida que eleve las voces hondureño-americanas en ambos países.' : 'Are you a college student, business owner, professional, community leader, wanting to learn more, or just looking to connect? Join our network and help build a shared platform elevating Honduran-American voices across both countries.'}
          </p>
          <motion.button onClick={() => setFormOpen(true)} whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.97
        }} className="inline-block bg-white text-haa-navy font-nunito font-bold text-base px-8 py-4 rounded-full hover:bg-haa-offwhite transition-colors shadow-xl" data-id="element-1874">
            {t('leadership.cta.button')}
          </motion.button>
        </motion.div>
      </div>

      <JoinNetworkForm isOpen={formOpen} onClose={() => setFormOpen(false)} data-id="element-1875" />
    </section>;
}